import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import googleFill from '@iconify/icons-eva/google-fill';
import twitterFill from '@iconify/icons-eva/twitter-fill';
import facebookFill from '@iconify/icons-eva/facebook-fill';
import linkedinFill from '@iconify/icons-eva/linkedin-fill';
import { Link as ScrollLink } from 'react-scroll';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
// material
import { experimentalStyled as styled } from '@material-ui/core/styles';
import { Grid, Link, Divider, Container, Typography, IconButton, Stack, Box, Button, Menu, MenuItem } from '@material-ui/core';
// routes
import { PATH_PAGE } from '../../routes/paths';
//
import Logo from '../../components/Logo';
import LegalModal from '../../components/LegalModal';
import skynicheLogo from '../../images/skyniche-logo-green.png';
import skynicheLogoWhite from '../../images/skyniche-logo-white.png';

// ----------------------------------------------------------------------

const SOCIALS = [
  { name: 'FaceBook', icon: facebookFill },
  { name: 'Google', icon: googleFill },
  { name: 'Linkedin', icon: linkedinFill },
  { name: 'Twitter', icon: twitterFill }
];

const LINKS = [
  {
    headline: 'Company',
    children: [
      { name: 'Home', action: 'home' },
      { name: 'Services', action: 'services' },
      { name: 'About us', href: PATH_PAGE.about },
      // { name: 'Contact us', href: PATH_PAGE.contact },
      { name: 'FAQs', href: '/#faqs' }
    ]
  },
  {
    headline: 'Legal',
    children: [
      { name: 'Terms and Condition', action: 'terms'},
      { name: 'Privacy Policy', action: 'privacy'}
    ]
  },
  {
    headline: 'Contact',
    children: [
      { name: 'Mayura Vahanam' },
      { name: '124, Thyagi Kumaran Street, Coimbatore, 641001' }
    ]
  }
];

const RootStyle = styled('div')(({ theme }) => ({
  position: 'relative',
  backgroundColor: theme.palette.background.default
}));

// ----------------------------------------------------------------------

export default function MainFooter() {
  const navigate = useNavigate();
  const [legalModalOpen, setLegalModalOpen] = useState(false);
  const [legalType, setLegalType] = useState('terms');
  const [isSkynicheHovered, setIsSkynicheHovered] = useState(false);
  const [servicesAnchorEl, setServicesAnchorEl] = useState(null);

  const handleOpenLegal = (type) => {
    setLegalType(type);
    setLegalModalOpen(true);
  };

  const handleHomeClick = () => {
    navigate('/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <RootStyle>
      <Divider />
      <Container maxWidth="lg" sx={{ pt: 10 }}>
        <Grid
          container
          justifyContent={{ xs: 'center', md: 'space-between' }}
          sx={{ textAlign: { xs: 'center', md: 'left' } }}
        >
          <Grid item xs={12} sx={{ mb: 3 }}>
            <ScrollLink to="move_top" spy smooth>
              <Logo sx={{ mx: { xs: 'auto', md: 'inherit' } }} />
            </ScrollLink>
          </Grid>
          <Grid item xs={8} md={3}>
            <Typography variant="body2" sx={{ pr: { md: 5 } }}>
             Delivering reliable logistics solutions that connect businesses and customers with speed, security, and efficiency. Mayura Logistics — your trusted partner for seamless deliveries.
            </Typography>

            <Stack
              spacing={1.5}
              direction="row"
              justifyContent={{ xs: 'center', md: 'flex-start' }}
              sx={{ mt: 5, mb: { xs: 5, md: 0 } }}
            >
              {SOCIALS.map((social) => (
                <IconButton key={social.name} color="primary" sx={{ p: 1 }}>
                  <Icon icon={social.icon} width={16} height={16} />
                </IconButton>
              ))}
            </Stack>
            
            <Box sx={{ mt: 3, mb: { xs: 5, md: 0 }, textAlign: { xs: 'center', md: 'left' } }}>
              <Button
                href="https://www.skyniche.com/"
                target="_blank"
                rel="noopener"
                onMouseEnter={() => setIsSkynicheHovered(true)}
                onMouseLeave={() => setIsSkynicheHovered(false)}
                startIcon={<Box component="img" src={isSkynicheHovered ? skynicheLogoWhite : skynicheLogo} sx={{ width: 24, height: 24 }} />}
                sx={{
                  borderRadius: 50,
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: 13,
                  px: 3,
                  py: 1,
                  color: 'primary.dark',
                  bgcolor: 'rgba(60, 184, 120, 0.1)',
                  border: '1px solid rgba(60, 184, 120, 0.2)',
                  boxShadow: '0 4px 12px rgba(60, 184, 120, 0.05)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    bgcolor: 'primary.main',
                    color: '#fff',
                    borderColor: 'primary.main',
                    boxShadow: '0 8px 20px rgba(60, 184, 120, 0.3)',
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                A skyniche Production
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12} md={7}>
            <Stack spacing={5} direction={{ xs: 'column', md: 'row' }} justifyContent="space-between">
              {LINKS.map((list) => {
                const { headline, children } = list;
                return (
                  <Stack key={headline} spacing={2}>
                    <Typography component="p" variant="overline">
                      {headline}
                    </Typography>
                    {children.map((link) => {
                      if (link.action) {
                        return (
                          <Link
                            key={link.name}
                            color="inherit"
                            variant="body2"
                            component="button"
                            onClick={(e) => {
                              if (link.action === 'terms') handleOpenLegal('terms');
                              if (link.action === 'privacy') handleOpenLegal('privacy');
                              if (link.action === 'home') handleHomeClick();
                              if (link.action === 'services') setServicesAnchorEl(e.currentTarget);
                            }}
                            sx={{ display: 'block', textAlign: { xs: 'center', md: 'left' }, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                          >
                            {link.name}
                          </Link>
                        );
                      }
                      return link.href ? (
                        <Link
                          to={link.href}
                          key={link.name}
                          color="inherit"
                          variant="body2"
                          component={RouterLink}
                          sx={{ display: 'block' }}
                        >
                          {link.name}
                        </Link>
                      ) : (
                        <Typography key={link.name} variant="body2" color="inherit" sx={{ display: 'block' }}>
                          {link.name}
                        </Typography>
                      );
                    })}
                  </Stack>
                );
              })}
            </Stack>
          </Grid>
        </Grid>

        <Typography
          component="p"
          variant="body2"
          sx={{
            mt: 10,
            pb: 5,
            fontSize: 13,
            textAlign: { xs: 'center', md: 'left' }
          }}
        >
          © 2026. All rights reserved
        </Typography>
      </Container>

      <LegalModal 
        open={legalModalOpen} 
        onClose={() => setLegalModalOpen(false)} 
        type={legalType} 
      />

      <Menu
        anchorEl={servicesAnchorEl}
        open={Boolean(servicesAnchorEl)}
        onClose={() => setServicesAnchorEl(null)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            ml: 1,
            boxShadow: (theme) => theme.customShadows.z20,
            borderRadius: 1.5,
            width: 200,
            border: '1px solid',
            borderColor: 'divider',
            p: 1
          }
        }}
      >
        <MenuItem 
          onClick={() => { 
            navigate('/services/national-delivery'); 
            setServicesAnchorEl(null); 
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          sx={{ 
            typography: 'subtitle2', 
            py: 1.5,
            px: 2,
            borderRadius: 1,
            transition: 'all 0.2s',
            '&:hover': {
              bgcolor: 'primary.lighter',
              color: 'primary.dark',
              transform: 'translateX(4px)'
            }
          }}
        >
          National Delivery
        </MenuItem>
        <MenuItem 
          onClick={() => { 
            navigate('/services/local-delivery'); 
            setServicesAnchorEl(null); 
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          sx={{ 
            typography: 'subtitle2', 
            py: 1.5,
            px: 2,
            borderRadius: 1,
            mt: 0.5,
            transition: 'all 0.2s',
            '&:hover': {
              bgcolor: 'primary.lighter',
              color: 'primary.dark',
              transform: 'translateX(4px)'
            }
          }}
        >
          Local Delivery
        </MenuItem>
      </Menu>
    </RootStyle>
  );
}
